<!-- modified primevue PanelMenu component -->
<template>
    <div class="panel-menu-height">
        <div class="p-panelmenu p-component">
            <template v-for="(item, index) in model" :key="label(item) + '_' + index">
                <div v-if="visible(item)" :class="getPanelClass(item)" :style="item.style">
                    <div :class="getHeaderClass(item)" :style="item.style">
                        <template v-if="!$slots.item">
                            <router-link
                                v-if="item.to && !disabled(item)"
                                :to="item.to"
                                custom
                                v-slot="{ navigate, href, isActive, isExactActive }"
                            >
                                <a
                                    :href="href"
                                    :class="getHeaderLinkClass(item, { isActive, isExactActive })"
                                    @click="onItemClick($event, item, navigate)"
                                    role="treeitem"
                                >
                                    <span v-if="item.icon" :class="getPanelIcon(item)"></span>
                                    <span class="p-menuitem-text">{{ label(item) }}</span>
                                </a>
                            </router-link>
                            <a
                                v-else
                                :href="item.url"
                                :class="getHeaderLinkClass(item)"
                                @click="onItemClick($event, item)"
                                :tabindex="disabled(item) ? null : '0'"
                                :aria-expanded="isActive(item)"
                                :id="ariaId + '_header_' + index"
                                :aria-controls="ariaId + '_content_' + index"
                            >
                                <span v-if="item.icon" :class="getPanelIcon(item)"></span>
                                <span class="p-menuitem-text">{{ label(item) }}</span>
                                <span v-if="item.items" :class="getPanelToggleIcon(item)"></span>
                            </a>
                        </template>
                        <component v-else :is="$slots.item" :item="item"></component>
                    </div>
                    <transition name="p-toggleable-content">
                        <div
                            class="p-toggleable-content"
                            v-show="isActive(item)"
                            role="region"
                            :id="ariaId + '_content_' + index"
                            :aria-labelledby="ariaId + '_header_' + index"
                        >
                            <div class="p-panelmenu-content" v-if="item.items">
                                <AppPanelMenuSub
                                    :model="item.items"
                                    class="p-panelmenu-root-submenu"
                                    :template="$slots.item"
                                    :expandedKeys="expandedKeys"
                                    @itemToggle="updateExpandedKeys"
                                    :exact="exact"
                                />
                            </div>
                        </div>
                    </transition>
                </div>
            </template>
        </div>
    </div>
    <div v-if="userConfirmed" class="kam-box p-2">
        <div class="flex justify-content-center">
            <div class="flex align-items-center justify-content-center">
                <p class="kam-box-text">Your account manager:</p>
            </div>
        </div>
        <div class="flex justify-content-center p-2">
            <div class="flex align-items-center justify-content-center">
                <Image
                    imageClass="kam-image"
                    :src="
                        selectedShop !== null &&
                        selectedShop?.sfu_image_url !== '' &&
                        selectedShop?.sfu_image_url !== null
                            ? selectedShop?.sfu_image_url
                            : 'images/avatar/kamAlt.png'
                    "
                    alt="Image"
                />
            </div>
        </div>
        <div class="flex justify-content-center">
            <div class="flex align-items-center justify-content-center">
                <p class="kam-account" style="cursor: text">
                    {{ selectedShop?.sfu_full_name }}
                </p>
            </div>
        </div>
        <div
            v-if="selectedShop?.sfu_title !== '' && selectedShop?.sfu_title !== null"
            class="flex justify-content-center"
        >
            <div class="flex align-items-center justify-content-center">
                <p class="kam-box-text" style="cursor: text">
                    {{ selectedShop?.sfu_title }}
                </p>
            </div>
        </div>
        <div class="flex align-items-center justify-content-center">
            <a class="kam-email" :href="`mailto:${selectedShop?.sfu_email}`">{{ selectedShop?.sfu_email }}</a>
        </div>
    </div>
</template>

<script>
import AppPanelMenuSub from "./AppPanelMenuSub";
import { UniqueComponentId } from "primevue/utils";
import { useShopStore } from "@/store/shop";
import { useUserStore } from "@/store/user";

export default {
    name: "AppPanelMenu",
    components: {
        AppPanelMenuSub,
    },
    emits: ["update:expandedKeys"],
    props: {
        model: {
            type: Array,
            default: null,
        },
        expandedKeys: {
            type: null,
            default: null,
        },
        exact: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            activeItem: null,
        };
    },
    methods: {
        onItemClick(event, item, navigate) {
            if (this.isActive(item) && this.activeItem === null) {
                this.activeItem = item;
            }

            if (this.disabled(item)) {
                event.preventDefault();
                return;
            }

            if (item.command) {
                item.command({
                    originalEvent: event,
                    item: item,
                });
            }

            if (this.activeItem && this.activeItem === item) {
                this.activeItem = null;
            } else {
                this.activeItem = item;
            }

            if (item.to && navigate) {
                navigate(event);
            }

            this.updateExpandedKeys({ item: item, expanded: this.activeItem !== null });
        },
        updateExpandedKeys(event) {
            if (this.expandedKeys) {
                let item = event.item;
                let _keys = { ...this.expandedKeys };

                if (_keys[item.key]) {
                    delete _keys[item.key];
                } else {
                    _keys[item.key] = true;
                }

                this.$emit("update:expandedKeys", _keys);
            }
        },
        getPanelClass(item) {
            return ["p-panelmenu-panel", item.class, this.isActive(item) ? "active" : ""];
        },
        getPanelToggleIcon(item) {
            const active = this.isActive(item);
            return ["mr-0 ml-auto p-panelmenu-icon pi", { "pi-chevron-down": !active, " pi-chevron-up": active }];
        },
        getPanelIcon(item) {
            return ["p-menuitem-icon", item.icon];
        },
        getHeaderLinkClass(item, routerProps) {
            return [
                "p-panelmenu-header-link",
                {
                    "router-link-active": routerProps && routerProps.isActive,
                    "router-link-active-exact": this.exact && routerProps && routerProps.isExactActive,
                },
            ];
        },
        isActive(item) {
            return this.expandedKeys ? this.expandedKeys[item.key] : item === this.activeItem;
        },
        getHeaderClass(item) {
            return [
                "p-component p-panelmenu-header",
                { "p-highlight": this.isActive(item), "p-disabled": this.disabled(item) },
            ];
        },
        visible(item) {
            return typeof item.visible === "function" ? item.visible() : item.visible !== false;
        },
        disabled(item) {
            return typeof item.disabled === "function" ? item.disabled() : item.disabled;
        },
        label(item) {
            return typeof item.label === "function" ? item.label() : item.label;
        },
    },
    computed: {
        ariaId() {
            return UniqueComponentId();
        },
        selectedShop() {
            return useShopStore().selectedShop;
        },
        userConfirmed() {
            return useUserStore().user.confirmed;
        },
    },
};
</script>

<style>
.p-panelmenu .p-panelmenu-header-link {
    display: flex;
    align-items: center;
    user-select: none;
    cursor: pointer;
    position: relative;
    text-decoration: none;
}

.p-panelmenu .p-panelmenu-header-link:focus {
    z-index: 1;
}

.p-panelmenu .p-submenu-list {
    margin: 0;
    padding: 0;
    list-style: none;
}

.p-panelmenu .p-menuitem-link {
    display: flex;
    align-items: center;
    user-select: none;
    cursor: pointer;
    text-decoration: none;
}

.p-panelmenu-panel {
    margin-bottom: 10px !important;
}

.kam-box {
    border-radius: 6px;
    border: 1px solid #67a5bb;
    margin-bottom: 10px;
    outline: none;
    padding: 0;
    position: relative;
    user-select: text;
}

.kam-box-text {
    color: #7a7a7a;
    font-size: 12px;
    font-weight: 400;
    padding-bottom: 3px;
    padding-top: 3px;
    text-align: center;
}

.kam-account {
    color: #000;
    font-size: 14px;
    font-weight: 500;
    padding-bottom: 3px;
    padding-top: 3px;
}

.kam-email {
    color: #000;
    font-size: 12px;
    font-weight: 500;
    line-break: anywhere;
    padding-bottom: 3px;
    text-align: center;
}

.panel-menu-height {
    margin-bottom: 10px;
}

.layout-sidebar .panel-menu-height {
    max-height: 100%;
}

.kam-image {
    border-radius: 50%;
    display: block;
    height: 80px;
    margin: auto;
    object-fit: contain;
    width: 80px;
}
</style>
